// Редовете на изписването като карти (под десктоп ширина): име · наличност
// (при рецепта — колко порции стигат) · ✕ (и „Раздели" при рецепта), после
// полета с етикет — Количество/Порции, Партида, Бележка — и ред „Ед. цена ·
// Нето · Бруто" (и за рецептата — по съставките ѝ). Не стига ли нещо за
// рецептата, в картата е кутията „Не стига" (WriteoffShortageBand). Полетата
// носят СЪЩИТЕ id-та (`wox-{ключ на реда}-{колона}`) като клетките на
// решетката, затова фокусът след добавяне и стрелките работят еднакво. Тук е и
// лентата над продуктите от разделена рецепта (обща с решетката).
import { Fragment } from "react";
import { Select } from "antd";
import { CloseOutlined, SearchOutlined, SplitCellsOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product, StockAsOf } from "../../../../api/inventory";
import { PickCell } from "../../components/PickCell";
import { ROW_DENSE } from "../../components/list/density";
import type { WriteoffTableProps } from "./WriteoffWorksheetTable";
import { ProductAvailabilityText, RecipeFitsText } from "./WriteoffStockTexts";
import { WriteoffShortageBand } from "./WriteoffShortageBand";
import type { RecipeRowStock } from "./useWriteoffStock";
import { shortageByProductOf, type StockShortage } from "./writeoffStockCheck";
import {
  cellId,
  formatMoney,
  formatPortionsInput,
  formatQtyInput,
  formatUnitPrice,
  lotLabelOf,
  parseDecimal,
  productLineValues,
  type WriteoffLineDraft,
} from "./writeoffWorksheetShared";

// Полетата на картата — с рамка (докосване с пръст), 16px срещу зуума на iOS
const FIELD_CLASS =
  "box-border w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-2.5 text-base leading-[normal] text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";

// Бутон-икона в ъгъла на картата (44px — удобен за пръст)
const ICON_BUTTON_CLASS =
  "flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-[7px] border border-shade-3 bg-surface text-body";

export const WriteoffLineCards = ({
  lines,
  productById,
  asOfByProduct,
  shortages,
  recipeStockByKey,
  vatRegistered,
  onPatchLine,
  onRequestRemove,
  onSplitRecipe,
  onFitRecipePortions,
  onRequestRevert,
  onSideExit,
}: WriteoffTableProps) => {
  const { t } = useTranslation();
  const shortageByProduct = shortageByProductOf(shortages);
  if (!lines.length)
    return (
      <div className="flex flex-col items-center gap-2.5 px-6 py-12 text-center">
        <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-brand-tint">
          <SearchOutlined className="text-xl text-brand-deep-ink" />
        </div>
        <div className="max-w-[420px] text-[13px] leading-[1.6] text-muted">{t("inventoryPage.woEmptyHint")}</div>
      </div>
    );

  return (
    <div>
      {lines.map((line, index) => {
        const previous = lines[index - 1];
        const showGroupHeader =
          line.groupId != null && (!previous || previous.groupId !== line.groupId);
        return (
          <Fragment key={line.key}>
            {showGroupHeader && <RecipeGroupHeader line={line} onRequestRevert={onRequestRevert} />}
            {line.kind === "recipe" ? (
              <RecipeCard
                line={line}
                index={index}
                stock={recipeStockByKey.get(line.key)}
                onPatchLine={onPatchLine}
                onRequestRemove={onRequestRemove}
                onSplitRecipe={onSplitRecipe}
                onFitPortions={onFitRecipePortions}
              />
            ) : (
              <ProductCard
                line={line}
                index={index}
                product={line.productId != null ? (productById.get(line.productId) ?? null) : null}
                asOf={line.productId != null ? (asOfByProduct.get(line.productId) ?? null) : null}
                shortage={line.productId != null ? shortageByProduct.get(line.productId) : undefined}
                vatRegistered={vatRegistered}
                onPatchLine={onPatchLine}
                onRequestRemove={onRequestRemove}
                onSideExit={onSideExit}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

// Лентата над продуктите от разделена рецепта: „Рецепта: X · ×2" + „Върни в рецепта"
export function RecipeGroupHeader({
  line,
  onRequestRevert,
}: {
  line: WriteoffLineDraft;
  onRequestRevert: (groupId: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center justify-between gap-2 border-b border-l-[3px] border-shade-2 border-l-brand bg-brand-tint px-3 ${ROW_DENSE}`}>
      <div className="min-w-0 truncate text-[11.5px] font-bold text-brand-deep-ink">
        {t("inventoryPage.recipeGroupLabel")}: {line.groupRecipeName}
        {line.groupPortions ? ` · ×${formatPortionsInput(line.groupPortions)}` : ""}
      </div>
      <button
        type="button"
        onClick={() => onRequestRevert(line.groupId as number)}
        className="inline-flex h-6 shrink-0 cursor-pointer items-center gap-[5px] whitespace-nowrap rounded-[7px] border border-shade-3 bg-surface px-2.5 text-[11.5px] font-bold text-brand-deep-ink hover:bg-panel pointer-coarse:h-9"
      >
        <UndoOutlined className="text-[11px]" />
        {t("inventoryPage.revertToRecipe")}
      </button>
    </div>
  );
}

const FieldLabel = ({ text }: { text: string }) => (
  <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">{text}</span>
);

// Бележката е еднаква за продукт и рецепта (колона 2)
const NoteField = ({
  line,
  onPatchLine,
}: {
  line: WriteoffLineDraft;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
}) => {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1">
      <FieldLabel text={t("inventoryPage.colNote")} />
      <input
        id={cellId(line.key, 2)}
        data-rk={line.key}
        data-c={2}
        value={line.note}
        placeholder={t("inventoryPage.notePh")}
        onChange={(e) => onPatchLine(line.key, { note: e.target.value })}
        className={`${FIELD_CLASS} placeholder:text-faint`}
      />
    </label>
  );
};

// ─── Продукт ────────────────────────────────────────────────────────────────
function ProductCard({
  line,
  index,
  product,
  asOf,
  shortage,
  vatRegistered,
  onPatchLine,
  onRequestRemove,
  onSideExit,
}: {
  line: WriteoffLineDraft;
  index: number;
  product: Product | null;
  asOf: StockAsOf | null;
  shortage: StockShortage | undefined;
  vatRegistered: boolean;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
  onRequestRemove: (key: number) => void;
  onSideExit: (key: number, col: number, step: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const { decimals, unit, available, lots, unitPrice, netSum, grossSum } = productLineValues(
    line,
    product,
    asOf,
    vatRegistered,
  );
  const selectedLot = line.lotId ? (lots.find((lot) => lot.id === line.lotId) ?? null) : null;
  const over = shortage != null;
  return (
    <div
      className={`flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3 ${
        line.groupId != null ? "border-l-[3px] border-l-brand" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-semibold text-ink">
            {index + 1}. {product?.name ?? "—"}
          </span>
          <span className={`text-xs ${over ? "font-bold text-danger-ink" : "text-muted"}`}>
            <ProductAvailabilityText
              line={line}
              available={available}
              decimals={decimals}
              unit={unit}
              shortage={shortage}
            />
          </span>
        </div>
        <button
          type="button"
          onClick={() => onRequestRemove(line.key)}
          aria-label={t("inventoryPage.confirmDeleteRowTitle")}
          className={ICON_BUTTON_CLASS}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
        <label className="flex flex-col gap-1">
          <FieldLabel text={`${t("inventoryPage.colQty")}${unit ? ` · ${unit}` : ""}`} />
          <input
            id={cellId(line.key, 0)}
            data-rk={line.key}
            data-c={0}
            value={line.quantity}
            placeholder="0"
            inputMode="decimal"
            enterKeyHint="next"
            onChange={(e) => onPatchLine(line.key, { quantity: e.target.value.replace(/[^0-9.,]/g, "") })}
            onFocus={(e) => e.target.select()}
            onBlur={() => {
              const parsed = parseDecimal(line.quantity);
              if (line.quantity !== "" && parsed > 0)
                onPatchLine(line.key, { quantity: formatQtyInput(parsed, decimals) });
            }}
            aria-invalid={over || undefined}
            className={`${FIELD_CLASS} text-right font-sheet font-semibold placeholder:text-faint ${
              over ? "!border-danger" : ""
            }`}
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <FieldLabel text={t("inventoryPage.colBatch")} />
          <PickCell
            id={cellId(line.key, 1)}
            rowKey={line.key}
            col={1}
            className={`${FIELD_CLASS} cursor-pointer truncate`}
            onSideExit={(step) => onSideExit(line.key, 1, step)}
            renderEditor={(close) => (
              <Select
                autoFocus
                defaultOpen
                value={line.lotId ? String(line.lotId) : "fifo"}
                onChange={(v) => {
                  onPatchLine(line.key, { lotId: v === "fifo" ? null : Number(v) });
                  close();
                }}
                onBlur={() => close(false)}
                popupMatchSelectWidth={false}
                options={[
                  { value: "fifo", label: t("inventoryPage.fifo") },
                  ...lots.map((lot) => ({ value: String(lot.id), label: lotLabelOf(lot, decimals, unit, t) })),
                ]}
                className="w-full"
              />
            )}
          >
            {selectedLot ? lotLabelOf(selectedLot, decimals, unit, t) : t("inventoryPage.fifo")}
          </PickCell>
        </label>
        <NoteField line={line} onPatchLine={onPatchLine} />
      </div>
      <CardValues unitPrice={unitPrice} netSum={netSum} grossSum={grossSum} />
    </div>
  );
}

// „Ед. цена · Нето · Бруто" под полетата — еднакво за продукт и рецепта
function CardValues({
  unitPrice,
  netSum,
  grossSum,
}: {
  unitPrice: number | null;
  netSum: number | null;
  grossSum: number | null;
}) {
  const { t } = useTranslation();
  const money = (value: number | null, format: (value: number) => string) =>
    value == null ? "—" : format(value);
  return (
    <div className="flex flex-wrap justify-end gap-x-2.5 gap-y-1 text-[12.5px] text-body">
      <span>
        {t("inventoryPage.colUnitPrice")}{" "}
        <span className="font-sheet text-ink">{money(unitPrice, formatUnitPrice)}</span>
      </span>
      <span>
        · {t("inventoryPage.colNet")} <span className="font-sheet text-ink">{money(netSum, formatMoney)}</span>
      </span>
      <span>
        · {t("inventoryPage.colGross")}{" "}
        <span className="font-sheet font-semibold text-ink">{money(grossSum, formatMoney)}</span>
      </span>
    </div>
  );
}

// ─── Рецепта (неразделена): порции + бележка; стойността — по съставките ────
function RecipeCard({
  line,
  index,
  stock,
  onPatchLine,
  onRequestRemove,
  onSplitRecipe,
  onFitPortions,
}: {
  line: WriteoffLineDraft;
  index: number;
  stock: RecipeRowStock | undefined;
  onPatchLine: (key: number, patch: Partial<WriteoffLineDraft>) => void;
  onRequestRemove: (key: number) => void;
  onSplitRecipe: (key: number) => void;
  onFitPortions: (key: number, portions: number) => void;
}) {
  const { t } = useTranslation();
  const short = (stock?.shortages.length ?? 0) > 0;
  return (
    <div className="flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink">
              {index + 1}. {line.recipeName ?? "—"}
            </span>
            <span className="shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] pb-[3px] pt-px text-[11px] font-semibold leading-[normal] text-brand-deep-ink">
              {t("inventoryPage.recipe")}
            </span>
          </span>
          <span className={`text-xs ${short ? "font-bold text-danger-ink" : "text-muted"}`}>
            <RecipeFitsText stock={stock} />
          </span>
        </div>
        <button
          type="button"
          onClick={() => onSplitRecipe(line.key)}
          aria-label={t("inventoryPage.splitToProducts")}
          title={t("inventoryPage.splitToProducts")}
          className={ICON_BUTTON_CLASS}
        >
          <SplitCellsOutlined />
        </button>
        <button
          type="button"
          onClick={() => onRequestRemove(line.key)}
          aria-label={t("inventoryPage.confirmDeleteRowTitle")}
          className={ICON_BUTTON_CLASS}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
        <label className="flex flex-col gap-1">
          <FieldLabel text={t("inventoryPage.portionsPh")} />
          <input
            id={cellId(line.key, 0)}
            data-rk={line.key}
            data-c={0}
            value={line.quantity}
            placeholder={t("inventoryPage.portionsPh")}
            inputMode="decimal"
            enterKeyHint="next"
            onChange={(e) => onPatchLine(line.key, { quantity: e.target.value.replace(/[^0-9.,]/g, "") })}
            onFocus={(e) => e.target.select()}
            onBlur={() => {
              const parsed = parseDecimal(line.quantity);
              if (line.quantity !== "" && parsed > 0)
                onPatchLine(line.key, { quantity: formatPortionsInput(parsed) });
            }}
            aria-invalid={short || undefined}
            className={`${FIELD_CLASS} text-right font-sheet font-semibold placeholder:text-faint ${
              short ? "!border-danger" : ""
            }`}
          />
        </label>
        <NoteField line={line} onPatchLine={onPatchLine} />
      </div>
      {short && stock && (
        <WriteoffShortageBand
          line={line}
          shortages={stock.shortages}
          fittingPortions={stock.fittingPortions}
          onFitPortions={onFitPortions}
          onSplitRecipe={onSplitRecipe}
          variant="card"
        />
      )}
      <CardValues
        unitPrice={stock?.values.unitPrice ?? null}
        netSum={stock?.values.netSum ?? null}
        grossSum={stock?.values.grossSum ?? null}
      />
    </div>
  );
}

export default WriteoffLineCards;
